export default function Workshops() {
  const workshops = [
    {
      title: 'AI Fundamentals for Business',
      duration: '4 weeks',
      level: 'Beginner',
      description: 'Perfect for teams with zero AI exposure. Learn the basics of AI, its applications, and how it can benefit your business.',
      topics: ['What is AI?', 'AI vs Traditional Software', 'Real-world AI applications', 'Getting started with AI tools'],
      image: 'https://public.readdy.ai/ai/img_res/561be72526c7b0765c7200b4a63b43b5.jpg'
    },
    {
      title: 'AI Tools Mastery',
      duration: '6 weeks',
      level: 'Intermediate',
      description: 'Hands-on training with popular AI tools like ChatGPT, Midjourney, and industry-specific AI applications.',
      topics: ['ChatGPT for business', 'AI image generation', 'AI writing assistants', 'Industry-specific tools'],
      image: 'https://readdy.ai/api/search-image?query=modern%20office%20workspace%20with%20computer%20screens%20displaying%20artificial%20intelligence%20software%20interfaces%20and%20dashboards%20professionals%20working%20with%20AI%20tools%20and%20applications%20bright%20contemporary%20office%20environment%20with%20technology%20focus%20clean%20organized%20workspace%20with%20multiple%20monitors&width=600&height=400&seq=workshop-002&orientation=landscape'
    },
    {
      title: 'Workflow Automation Workshop',
      duration: '8 weeks',
      level: 'Advanced',
      description: 'Design and implement AI-powered workflows that automate repetitive tasks and boost productivity.',
      topics: ['Process mapping', 'Automation strategies', 'Integration techniques', 'ROI measurement'],
      image: 'https://readdy.ai/api/search-image?query=futuristic%20digital%20workflow%20automation%20visualization%20with%20connected%20nodes%20and%20process%20flows%20on%20modern%20computer%20interface%20showing%20automated%20systems%20and%20data%20pipelines%20clean%20professional%20technology%20workspace%20with%20multiple%20screens%20displaying%20workflow%20diagrams%20and%20automation%20dashboards&width=600&height=400&seq=workshop-003&orientation=landscape'
    },
    {
      title: 'AI Strategy & Implementation',
      duration: '12 weeks',
      level: 'Executive',
      description: 'Strategic planning for AI adoption, change management, and long-term AI integration roadmaps.',
      topics: ['AI strategy development', 'Change management', 'Team training plans', 'Success metrics'],
      image: 'https://readdy.ai/api/search-image?query=executive%20business%20meeting%20with%20professionals%20discussing%20strategy%20around%20conference%20table%20with%20presentation%20displays%20showing%20business%20analytics%20and%20growth%20charts%20modern%20corporate%20boardroom%20with%20natural%20lighting%20collaborative%20planning%20session%20with%20diverse%20leadership%20team&width=600&height=400&seq=workshop-004&orientation=landscape'
    }
  ];

  return (
    <section id="workshops" className="py-24 bg-[#f8f8f9]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-block mb-4 px-4 py-2 bg-navy/10 rounded-md">
            <span className="text-navy text-base font-semibold">Getting Started</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-navy">
            Start With the <span className="text-gold">Foundations Program</span>
          </h2>
          <p className="text-lg text-charcoal max-w-3xl mx-auto">
            The Foundations Program is the starting point for every organization we work with. The goal is confidence, not automation. We delivery this through flexible training workshops tailored to your team’s needs.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="bg-white p-8 rounded-2xl border border-soft-gray/30 shadow-sm hover:shadow-xl transition-all">
            <div className="w-16 h-16 flex items-center justify-center bg-navy/10 rounded-xl mb-6">
              <i className="ri-lightbulb-line text-3xl text-navy"></i>
            </div>
            <h3 className="text-xl font-bold mb-3 text-navy">What AI Can Do Today</h3>
            <p className="text-charcoal leading-relaxed">
              Learn what AI can realistically help with in your daily work, without the hype or unrealistic promises.
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-soft-gray/30 shadow-sm hover:shadow-xl transition-all">
            <div className="w-16 h-16 flex items-center justify-center bg-navy/10 rounded-xl mb-6">
              <i className="ri-chat-3-line text-3xl text-navy"></i>
            </div>
            <h3 className="text-xl font-bold mb-3 text-navy">Clear Communication</h3>
            <p className="text-charcoal leading-relaxed">
              Master how to communicate clearly with AI tools to get the results you need every time.
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-soft-gray/30 shadow-sm hover:shadow-xl transition-all">
            <div className="w-16 h-16 flex items-center justify-center bg-navy/10 rounded-xl mb-6">
              <i className="ri-task-line text-3xl text-navy"></i>
            </div>
            <h3 className="text-xl font-bold mb-3 text-navy">Everyday Applications</h3>
            <p className="text-charcoal leading-relaxed">
              Apply AI to everyday tasks like writing, summarizing, organizing, planning, and more.
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-navy to-[#062299] p-12 rounded-3xl shadow-2xl text-center mb-12">
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Organizations can choose between a half-day or full-day workshop based on their goals and level of readiness. Following training, some clients elect to have us develop a customized AI assistant to further support their team’s workflows.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="/workshops/half-day"
              className="inline-block px-6 sm:px-8 py-3 sm:py-4 bg-gold text-white text-sm sm:text-base font-semibold rounded-full hover:bg-[#a68a4e] transition-all shadow-lg hover:shadow-xl cursor-pointer text-center whitespace-nowrap"
            >
              Half Day Workshop →
            </a>
            <a
              href="/workshops/full-day"
              className="inline-block px-6 sm:px-8 py-3 sm:py-4 bg-white text-navy text-sm sm:text-base font-semibold rounded-full hover:bg-soft-gray transition-all shadow-lg hover:shadow-xl cursor-pointer text-center whitespace-nowrap"
            >
              Full Day Workshop →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
